<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'next/icon/Icon.vue';
import { resolveSavedIconClass } from 'dashboard/helper/legacyIconClass';

export default {
  components: {
    NextButton,
    Checkbox,
    Icon,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
    },
  },
  data() {
    return {
      isLoading: false,
      app: {
        title: '',
        content: {
          type: 'frame',
          url: '',
        },
        show_in_navigation: false,
        icon: 'i-ri-link',
      },
      iconOptions: [
        { label: 'Link', value: 'i-ri-link' },
        { label: 'Layout Grid', value: 'i-ri-layout-grid-line' },
        { label: 'Chart', value: 'i-ri-line-chart-line' },
        { label: 'Inbox', value: 'i-ri-inbox-line' },
        { label: 'Message Circle', value: 'i-ri-chat-1-line' },
        { label: 'Bot', value: 'i-ri-robot-line' },
        { label: 'Megaphone', value: 'i-ri-megaphone-line' },
        { label: 'Workflow', value: 'i-ri-flow-chart' },
        { label: 'Painel', value: 'i-ri-layout-bottom-2-line' },
        { label: 'Bar Chart', value: 'i-ri-bar-chart-grouped-line' },
        { label: 'Users', value: 'i-ri-group-line' },
        { label: 'User Cog', value: 'i-ri-user-settings-line' },
        { label: 'Briefcase', value: 'i-ri-briefcase-line' },
        { label: 'Building', value: 'i-ri-building-2-line' },
        { label: 'Library Big', value: 'i-ri-book-shelf-line' },
        { label: 'Blocks', value: 'i-ri-apps-2-line' },
        { label: 'Tag', value: 'i-ri-price-tag-line' },
        { label: 'Tags', value: 'i-ri-price-tag-2-line' },
        { label: 'Badge Check', value: 'i-ri-verified-badge-line' },
        { label: 'Shield', value: 'i-ri-shield-line' },
        { label: 'Shield Check', value: 'i-ri-shield-check-line' },
        { label: 'Bolt', value: 'i-ri-flashlight-line' },
        { label: 'Rocket', value: 'i-ri-rocket-line' },
        { label: 'Flame', value: 'i-ri-fire-line' },
        { label: 'Sparkles', value: 'i-ri-sparkling-line' },
        { label: 'Wand Sparkles', value: 'i-ri-magic-line' },
        { label: 'Star', value: 'i-ri-star-line' },
        { label: 'Heart', value: 'i-ri-heart-line' },
        { label: 'Clock', value: 'i-ri-time-line' },
        { label: 'Calendar Days', value: 'i-ri-calendar-2-line' },
        { label: 'File Text', value: 'i-ri-file-text-line' },
        { label: 'Folder', value: 'i-ri-folder-line' },
        { label: 'Search', value: 'i-ri-search-line' },
        { label: 'Settings', value: 'i-ri-settings-line' },
        { label: 'Cog', value: 'i-ri-settings-3-line' },
        { label: 'Sliders Horizontal', value: 'i-ri-equalizer-line' },
        { label: 'Message Square Quote', value: 'i-ri-chat-quote-line' },
        { label: 'Messages Square', value: 'i-ri-chat-3-line' },
        { label: 'Phone', value: 'i-ri-phone-line' },
        { label: 'Mail', value: 'i-ri-mail-line' },
        { label: 'Globe', value: 'i-ri-globe-line' },
        { label: 'Laptop', value: 'i-ri-computer-line' },
        { label: 'Smartphone', value: 'i-ri-smartphone-line' },
        { label: 'Puzzle', value: 'i-ri-puzzle-line' },
        { label: 'Package', value: 'i-ri-box-1-line' },
        { label: 'Credit Card', value: 'i-ri-bank-card-line' },
        { label: 'Banknote', value: 'i-ri-cash-line' },
        { label: 'Shopping Cart', value: 'i-ri-shopping-cart-line' },
        { label: 'Truck', value: 'i-ri-truck-line' },
      ],
    };
  },
  computed: {
    header() {
      return this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.HEADER`);
    },
    submitButtonLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.FORM_SUBMIT`
      );
    },
    selectedIconOption() {
      // Traduz de novo aqui: `app.icon` pode ter sido semeado por fora do
      // mounted (ex.: reabrir o modal) e um valor legado não pode cair no
      // primeiro item do catálogo como se nada tivesse sido escolhido.
      const icon = resolveSavedIconClass(this.app.icon);
      return (
        this.iconOptions.find(option => option.value === icon) ||
        this.iconOptions[0]
      );
    },
  },
  mounted() {
    if (this.mode === 'UPDATE' && this.selectedAppData) {
      this.app.title = this.selectedAppData.title;
      this.app.content = this.selectedAppData.content[0];
      this.app.show_in_navigation = !!this.selectedAppData.show_in_navigation;
      // SiteUP V2 (F1.2e): app criado antes da migração tem `i-lucide-*` no
      // banco. Traduz ao abrir para o catálogo Remix marcar o equivalente como
      // selecionado — mesmo ícone que a sidebar já desenha. Regrava só se o
      // usuário salvar; nenhuma migração de dados foi feita.
      this.app.icon = resolveSavedIconClass(
        this.selectedAppData.icon || 'i-ri-link'
      );
    }
  },
  methods: {
    selectIcon(iconValue) {
      this.app.icon = iconValue;
      this.$refs.iconDropdown?.removeAttribute('open');
    },
    closeModal() {
      // Reset the data once closed
      this.app = {
        title: '',
        content: { type: 'frame', url: '' },
        show_in_navigation: false,
        icon: 'i-ri-link',
      };
      this.$emit('close');
    },
    async submit() {
      try {
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }

        const action = this.mode.toLowerCase();
        const payload = {
          title: this.app.title,
          content: [this.app.content],
          show_in_navigation: !!this.app.show_in_navigation,
          icon: this.app.icon || 'i-ri-link',
        };

        if (action === 'update') {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        useAlert(
          this.$t(
            `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_SUCCESS`
          )
        );
        this.closeModal();
      } catch (err) {
        useAlert(
          this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_ERROR`)
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-[42rem] overflow-visible">
      <woot-modal-header :header-title="header" />
      <form
        class="w-full flex flex-col flex-1 min-h-0"
        @submit.prevent="submit"
      >
        <div class="flex-1 overflow-visible">
          <woot-input
            v-model="app.title"
            :class="{ error: v$.app.title.$error }"
            class="w-full"
            :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
            "
            :error="
              v$.app.title.$error
                ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
                : null
            "
            data-testid="app-title"
            @input="v$.app.title.$touch"
            @blur="v$.app.title.$touch"
          />
          <woot-input
            v-model="app.content.url"
            :class="{ error: v$.app.content.url.$error }"
            class="w-full"
            :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
            "
            :error="
              v$.app.content.url.$error
                ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
                : null
            "
            data-testid="app-url"
            @input="v$.app.content.url.$touch"
            @blur="v$.app.content.url.$touch"
          />
          <div class="flex items-center mt-4 mb-2">
            <Checkbox v-model="app.show_in_navigation" />
            <label
              class="ml-2 cursor-pointer select-none text-sm text-n-slate-11"
            >
              {{
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SHOW_IN_NAVIGATION_LABEL',
                  'Adicionar ao menu lateral'
                )
              }}
            </label>
          </div>
          <div v-if="app.show_in_navigation" class="mb-2">
            <label class="block text-sm font-medium text-n-slate-11 mb-2">
              {{
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SIDEBAR_ICON_LABEL',
                  'Icone do menu lateral'
                )
              }}
            </label>
            <div class="flex items-center gap-2">
              <details ref="iconDropdown" class="relative w-full">
                <summary
                  class="list-none w-full rounded-xl border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 cursor-pointer flex items-center justify-between"
                  data-testid="app-sidebar-icon"
                >
                  <span class="flex items-center gap-2">
                    <Icon :icon="selectedIconOption.value" class="size-4" />
                    <span>{{ selectedIconOption.label }}</span>
                  </span>
                  <span class="i-ri-arrow-down-s-line size-4 text-n-slate-10" />
                </summary>
                <div
                  class="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-n-weak bg-n-background shadow-lg p-1"
                >
                  <button
                    v-for="iconOption in iconOptions"
                    :key="iconOption.value"
                    type="button"
                    class="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
                    @click="selectIcon(iconOption.value)"
                  >
                    <span class="flex items-center gap-2">
                      <Icon :icon="iconOption.value" class="size-4" />
                      <span>{{ iconOption.label }}</span>
                    </span>
                    <span
                      v-if="app.icon === iconOption.value"
                      class="rounded-md px-1.5 py-0.5 text-[10px] leading-4 font-medium uppercase tracking-wide bg-n-blue-text text-n-alpha-white"
                    >
                      Selecionado
                    </span>
                  </button>
                </div>
              </details>
            </div>
          </div>
        </div>
        <div class="mt-auto flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL')
            "
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="submitButtonLabel"
            :disabled="v$.$invalid"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
